/**
 * Event Generation Logic for Bulk Events
 * 
 * Generates event lists based on bulk event configuration.
 * All times are stored in UTC and converted from local browser time.
 */

import { CreateEventDto } from '../api/rest-client';
import { addDays, addWeeks, getFirstMondayOfWeek } from './dateUtils';
import type { DayConfig, BulkEventsConfig } from './bulkEventsValidation';

/**
 * Generate a list of events based on bulk configuration
 * @param config - Bulk events configuration
 * @param season - Current season identifier
 * @returns Array of CreateEventDto objects ready for API submission
 */
export function generateEventsList(
  config: BulkEventsConfig,
  season: string
): CreateEventDto[] {
  const events: CreateEventDto[] = [];
  
  // Parse date range in local timezone
  // Split ISO date string and use Date constructor to avoid UTC interpretation
  const [startYear, startMonth, startDay] = config.startDate.split('-').map(Number);
  const startDate = new Date(startYear, startMonth - 1, startDay);
  
  const [endYear, endMonth, endDay] = config.endDate.split('-').map(Number);
  const endDate = new Date(endYear, endMonth - 1, endDay);
  
  // Get enabled days
  const enabledDays = config.weeklySchedule.filter(day => day.enabled);
  
  if (enabledDays.length === 0) {
    return [];
  }
  
  // Start from the first Monday of the start date's week
  let currentWeek = getFirstMondayOfWeek(startDate);
  
  // Generate events for each occurrence
  while (currentWeek <= endDate) {
    for (const dayConfig of enabledDays) {
      // Calculate the date for this day of the week
      // dayOfWeek: 0=Sunday, 1=Monday, ..., 6=Saturday
      // currentWeek is Monday, so:
      // Monday (1) = +0 days, Tuesday (2) = +1 day, ..., Saturday (6) = +5 days
      // Sunday (0) = +6 days (next Sunday after Monday)
      let daysFromMonday;
      if (dayConfig.dayOfWeek === 0) {
        // Sunday is 6 days after Monday
        daysFromMonday = 6;
      } else {
        // Other days: Monday=0, Tuesday=1, etc.
        daysFromMonday = dayConfig.dayOfWeek - 1;
      }
      
      const eventDate = addDays(currentWeek, daysFromMonday);
      
      // Only include if within date range
      if (eventDate >= startDate && eventDate <= endDate) {
        events.push(createEventDto(eventDate, dayConfig, season));
      }
    }
    
    // Move to next occurrence
    currentWeek = addWeeks(currentWeek, config.recurrenceWeeks);
  }
  
  // Sort events chronologically by start time
  return events.sort((a, b) => 
    new Date(a.event_starttime).getTime() - new Date(b.event_starttime).getTime()
  );
}

/**
 * Create a single event DTO from date and day configuration
 * Converts local browser time to UTC for storage
 * @param date - The date for the event
 * @param dayConfig - Configuration for this day
 * @param season - Current season identifier
 * @returns CreateEventDto ready for API submission
 */
export function createEventDto(
  date: Date,
  dayConfig: DayConfig,
  _season: string
): CreateEventDto {
  // Parse time components
  const [startHour, startMin] = dayConfig.startTime.split(':').map(Number);
  const [endHour, endMin] = dayConfig.endTime.split(':').map(Number);
  
  // Create datetime in local timezone
  const startDateTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    startHour,
    startMin,
    0,
    0
  );
  
  const endDateTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    endHour,
    endMin,
    0,
    0
  );
  
  // Convert to UTC ISO strings for storage
  // The Date object automatically handles timezone conversion
  const event: CreateEventDto = {
    event_id: crypto.randomUUID(),
    event_name: dayConfig.eventName.trim(),
    event_type: dayConfig.eventType,
    event_location: dayConfig.locationId, // This is location_id (UUID)
    event_starttime: startDateTime.toISOString(), // Stored in UTC
    event_endtime: endDateTime.toISOString(), // Stored in UTC
    event_status: 'active',
  };
  
  // Optional fields based on event type
  // These are set to undefined if not applicable, matching EventForm behavior
  if (dayConfig.eventType === 'Time Trial') {
    event.trail_configuration_id = undefined;
  }
  
  if (dayConfig.eventType === 'Race') {
    event.official_results_url = undefined;
  }
  
  return event;
}

/**
 * Calculate the total number of events that will be generated
 * Useful for preview functionality
 * @param config - Bulk events configuration
 * @returns Number of events that will be created
 */
export function calculateEventCount(config: BulkEventsConfig): number {
  // Parse date range in local timezone
  const [startYear, startMonth, startDay] = config.startDate.split('-').map(Number);
  const startDate = new Date(startYear, startMonth - 1, startDay);
  
  const [endYear, endMonth, endDay] = config.endDate.split('-').map(Number);
  const endDate = new Date(endYear, endMonth - 1, endDay);
  
  const enabledDays = config.weeklySchedule.filter(day => day.enabled);
  
  if (enabledDays.length === 0) {
    return 0;
  }
  
  let count = 0;
  let currentWeek = getFirstMondayOfWeek(startDate);
  
  while (currentWeek <= endDate) {
    for (const dayConfig of enabledDays) {
      const daysFromMonday = dayConfig.dayOfWeek === 0 ? 6 : dayConfig.dayOfWeek - 1;
      const eventDate = addDays(currentWeek, daysFromMonday);
      
      if (eventDate >= startDate && eventDate <= endDate) {
        count++;
      }
    }
    
    currentWeek = addWeeks(currentWeek, config.recurrenceWeeks);
  }
  
  return count;
}
